export const PERU_TIME_ZONE = 'America/Lima';

const getDateTimeParts = (date, timeZone) => new Intl.DateTimeFormat('en-CA', {
  timeZone,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
})
  .formatToParts(date)
  .reduce((result, part) => ({ ...result, [part.type]: part.value }), {});

export const getCurrentPeruDate = (date = new Date()) => {
  const parsedDate = date instanceof Date ? date : new Date(date);
  if (isNaN(parsedDate.getTime())) return '';

  const parts = getDateTimeParts(parsedDate, PERU_TIME_ZONE);
  return `${parts.year}-${parts.month}-${parts.day}`;
};

// Formateadores de fecha
export const formatDate = (date, format = 'DD/MM/YYYY', timeZone) => {
  if (!date) return '';
  
  let d;
  let dateTimeParts;
  // Si la fecha es solo YYYY-MM-DD, la parseamos manualmente para evitar desfase UTC
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-').map(Number);
    d = new Date(year, month - 1, day);
  } else {
    d = new Date(date);
  }

  if (isNaN(d.getTime())) return '';

  if (timeZone && !(typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date))) {
    dateTimeParts = getDateTimeParts(d, timeZone);
  }

  const day = dateTimeParts?.day || String(d.getDate()).padStart(2, '0');
  const month = dateTimeParts?.month || String(d.getMonth() + 1).padStart(2, '0');
  const year = dateTimeParts?.year || String(d.getFullYear());
  const hours = dateTimeParts?.hour || String(d.getHours()).padStart(2, '0');
  const minutes = dateTimeParts?.minute || String(d.getMinutes()).padStart(2, '0');
  const yearShort = year.slice(-2);
  const seconds = dateTimeParts?.second || String(d.getSeconds()).padStart(2, '0');
  
  switch (format) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'DD/MM/YYYY HH:mm':
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    case 'DD-MM-YY HH:mm:ss':
      return `${day}-${month}-${yearShort} ${hours}:${minutes}:${seconds}`;
    case 'HH:mm':
      return `${hours}:${minutes}`;
    case 'HH:mm:ss':
      return `${hours}:${minutes}:${seconds}`;
    case 'relative':
      return getRelativeTime(d);
    default:
      return d.toLocaleDateString('es-PE', timeZone ? { timeZone } : undefined);
  }
};

// Tiempo relativo (ej: "hace 2 minutos")
export const getRelativeTime = (date) => {
  const now = new Date();
  const diff = now - new Date(date);
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (seconds < 60) return 'hace unos segundos';
  if (minutes < 60) return `hace ${minutes} minuto${minutes > 1 ? 's' : ''}`;
  if (hours < 24) return `hace ${hours} hora${hours > 1 ? 's' : ''}`;
  if (days < 7) return `hace ${days} día${days > 1 ? 's' : ''}`;
  
  return formatDate(date);
};

// Formateadores de números
export const formatNumber = (number, decimals = 0) => {
  if (number === null || number === undefined) return '';
  return new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number);
};

// Formateador de moneda
export const formatCurrency = (amount, currency = 'PEN') => {
  if (amount === null || amount === undefined) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

// Formateador de porcentaje
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined) return '';
  return `${formatNumber(value, decimals)}%`;
};

// Formateador de tamaño de archivo
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Capitalizar primera letra
export const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Truncar texto
export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Formatear nombre completo
export const formatFullName = (firstName, lastName) => {
  return [firstName, lastName].filter(Boolean).join(' ');
};

// Formatear teléfono
export const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phone;
};

// Generar iniciales
export const getInitials = (name) => {
  if (!name) return '';
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

// Formatear estado/status
export const formatStatus = (status) => {
  const statusMap = {
    active: 'Activo',
    inactive: 'Inactivo',
    pending: 'Pendiente',
    approved: 'Aprobado',
    rejected: 'Rechazado',
    draft: 'Borrador',
    published: 'Publicado',
  };
  
  return statusMap[status] || capitalize(status);
};

// Obtener color por estado
export const getStatusColor = (status) => {
  const colorMap = {
    active: 'success',
    inactive: 'default',
    pending: 'warning',
    approved: 'success',
    rejected: 'error',
    draft: 'info',
    published: 'success',
  };
  
  return colorMap[status] || 'default';
};
